import { Controller } from "@hotwired/stimulus"

// Formats a time element in browser timezone using native Intl API.
// Supports two display modes when location timezone differs from browser timezone:
//
// Mode "badge" (default, for detail pages):
//   - Shows UTC time on hover (tooltip)
//   - Displays a badge with location time when timezones differ
//
// Mode "hover" (for datagrids):
//   - Adds underline indicator when timezones differ
//   - On hover, swaps displayed time to show location time
//
// Usage:
//   <time data-controller="browser-time"
//         datetime="2024-01-15T10:30:00Z"
//         data-browser-time-location-timezone-value="America/Los_Angeles"
//         data-browser-time-location-time-str-value="01/15/2024 02:30AM PST"
//         data-browser-time-mode-value="hover">
//   </time>
export default class extends Controller {
  static values = {
    locationTimezone: String,
    locationTimeStr: String,
    mode: { type: String, default: "badge" }
  }

  connect() {
    this.browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    this.formatTime()
  }

  formatTime() {
    const datetime = this.element.getAttribute("datetime")
    if (!datetime) return

    const date = new Date(datetime)
    if (isNaN(date.getTime())) return

    this.browserFormattedTime = this.formatInBrowserTimezone(date)
    this.element.textContent = this.browserFormattedTime
    this.element.setAttribute("data-localized", "true")

    const locationTz = this.locationTimezoneValue
    if (!locationTz) {
      this.addUtcTooltip(date)
      return
    }

    const diff = this.calculateHourDifference(date, locationTz)
    if (diff === 0) {
      this.addUtcTooltip(date)
      return
    }

    if (this.modeValue === "hover") {
      this.setupHoverMode(date)
    } else {
      this.addUtcTooltip(date)
      this.addLocationTimeBadge(diff)
    }
  }

  formatInBrowserTimezone(date) {
    const options = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZoneName: "short",
      timeZone: this.browserTimezone
    }

    const formatted = new Intl.DateTimeFormat("en-US", options).format(date)
    return formatted.replace(",", "")
  }

  addUtcTooltip(date) {
    const utcTime = date.toISOString().replace("T", " ").substring(0, 19) + " UTC"
    this.element.setAttribute("title", utcTime)
  }

  setupHoverMode(date) {
    this.element.classList.add("tz-differs")

    const locationTimeStr = this.locationTimeStrValue || this.formatInLocationTimezone(date)

    this.element.addEventListener("mouseenter", () => {
      this.element.textContent = locationTimeStr
    })

    this.element.addEventListener("mouseleave", () => {
      this.element.textContent = this.browserFormattedTime
    })
  }

  formatInLocationTimezone(date) {
    const options = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZoneName: "short",
      timeZone: this.locationTimezoneValue
    }

    try {
      const formatted = new Intl.DateTimeFormat("en-US", options).format(date)
      return formatted.replace(",", "")
    } catch (error) {
      console.warn(`[browser-time] Invalid timezone: ${this.locationTimezoneValue}`, error)
      return this.browserFormattedTime
    }
  }

  addLocationTimeBadge(diff) {
    const container = this.element.closest("td") || this.element.parentElement
    if (!container || container.querySelector(".tz-diff")) return

    if (this.locationTimeStrValue) {
      const timeBadge = document.createElement("span")
      timeBadge.className = "time-badge"
      timeBadge.textContent = this.locationTimeStrValue
      container.appendChild(timeBadge)
    }

    const sign = diff > 0 ? "+" : ""
    const diffBadge = document.createElement("span")
    diffBadge.className = "time-badge tz-diff"
    diffBadge.textContent = `${sign}${diff}h`
    container.appendChild(diffBadge)
  }

  calculateHourDifference(date, locationTz) {
    const browserOffset = this.getTimezoneOffset(date, this.browserTimezone)
    const locationOffset = this.getTimezoneOffset(date, locationTz)

    let diff = Math.round((locationOffset - browserOffset) / 60)

    if (diff > 12) diff -= 24
    if (diff < -12) diff += 24

    return diff
  }

  getTimezoneOffset(date, timezone) {
    try {
      const utcDate = new Date(date.toLocaleString("en-US", { timeZone: "UTC" }))
      const tzDate = new Date(date.toLocaleString("en-US", { timeZone: timezone }))
      return (tzDate - utcDate) / 60000
    } catch (error) {
      console.warn(`[browser-time] Invalid timezone: ${timezone}`, error)
      return 0
    }
  }
}
